import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { api, query } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { formatDateTime, methodLabel, METHOD_LABELS } from "../../lib/format";
import type { ListResponse, Payment } from "../../lib/types";
import {
  EmptyState,
  ErrorNotice,
  PageHeader,
  Pagination,
  Sheet,
  Skeleton,
  StatusPill,
  Table,
  Td,
  Th,
  Tr,
} from "../../components/ui";

const PAGE_SIZE = 25;

const STATUS_FILTERS = [
  { value: "", label: "All statuses" },
  { value: "succeeded", label: "Succeeded" },
  { value: "awaiting_payment", label: "Awaiting payment" },
  { value: "awaiting_otp", label: "Awaiting OTP" },
  { value: "requires_action", label: "Requires action" },
  { value: "pending", label: "Pending" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "expired", label: "Expired" },
];

export function Payments() {
  const { mode } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [method, setMethod] = useState("");
  const [reference, setReference] = useState("");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);

  const list = useQuery({
    queryKey: ["payments", mode, status, method, reference, offset],
    queryFn: () =>
      api.get<ListResponse<Payment>>(
        `/api/dashboard/payments${query({
          mode,
          status,
          method,
          reference,
          limit: PAGE_SIZE,
          offset,
        })}`,
      ),
  });

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    setReference(search.trim());
    setOffset(0);
  }

  const rows = list.data?.data ?? [];

  return (
    <>
      <PageHeader
        eyebrow={mode === "live" ? "Live" : "Sandbox"}
        title="Payments"
        description="Every payment your customers have started, with the fee applied to each."
      />

      {/* Filters sit in one row above the table, so the controls that change what is
          shown are never mixed into the data itself. */}
      <div className="settle settle-1 mb-4 flex flex-wrap items-center gap-2">
        <form onSubmit={submitSearch} className="flex min-w-[200px] flex-1 gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Find by your reference…"
            aria-label="Search by reference"
            className="w-full rounded-md border border-rule-strong bg-paper-raised px-3 py-1.5 text-[13px] placeholder:text-ink-faint focus:border-verdigris-bright focus:outline-none focus:ring-2 focus:ring-verdigris-bright/15"
          />
        </form>

        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setOffset(0);
          }}
          aria-label="Filter by status"
          className="rounded-md border border-rule-strong bg-paper-raised px-2.5 py-1.5 text-[13px]"
        >
          {STATUS_FILTERS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <select
          value={method}
          onChange={(e) => {
            setMethod(e.target.value);
            setOffset(0);
          }}
          aria-label="Filter by payment method"
          className="rounded-md border border-rule-strong bg-paper-raised px-2.5 py-1.5 text-[13px]"
        >
          <option value="">All methods</option>
          {Object.entries(METHOD_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>

        {(status || method || reference) && (
          <button
            onClick={() => {
              setStatus("");
              setMethod("");
              setReference("");
              setSearch("");
              setOffset(0);
            }}
            className="text-[12.5px] text-ink-muted underline underline-offset-2 hover:text-ink"
          >
            Clear
          </button>
        )}
      </div>

      <ErrorNotice error={list.error} className="mb-4" />

      <Sheet className="settle settle-2 overflow-hidden">
        {list.isLoading ? (
          <Skeleton rows={8} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No payments match"
            description={
              status || method || reference
                ? "Try clearing the filters."
                : "Payments will appear here as your customers pay."
            }
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Reference</Th>
                  <Th>Method</Th>
                  <Th>Status</Th>
                  <Th align="right">Amount</Th>
                  <Th align="right">Fee</Th>
                  <Th align="right">Net</Th>
                  <Th align="right">Created</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((payment) => (
                  <Tr key={payment.id} onClick={() => navigate(`/payments/${payment.id}`)}>
                    <Td>
                      <span className="font-mono text-[12px] text-ink">{payment.reference}</span>
                      {payment.item_name && (
                        <span className="mt-0.5 block max-w-[220px] truncate text-[11.5px] text-ink-faint">
                          {payment.item_name}
                        </span>
                      )}
                    </Td>
                    <Td className="whitespace-nowrap text-ink-muted">
                      {methodLabel(payment.method)}
                    </Td>
                    <Td>
                      <StatusPill status={payment.status} />
                    </Td>
                    <Td align="right" mono>
                      {payment.charged.formatted}
                    </Td>
                    <Td align="right" mono className="text-brass">
                      {payment.platform_fee.formatted}
                    </Td>
                    <Td align="right" mono className="font-medium">
                      {payment.status === "succeeded" ? payment.net.formatted : "—"}
                    </Td>
                    <Td align="right" className="whitespace-nowrap text-[12px] text-ink-muted">
                      {formatDateTime(payment.created_at)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              total={list.data?.total ?? 0}
              limit={PAGE_SIZE}
              offset={offset}
              onChange={setOffset}
            />
          </>
        )}
      </Sheet>

      <p className="mt-4 text-[12px] text-ink-muted">
        Fees are shown per payment.{" "}
        <Link to="/pricing" className="text-verdigris underline underline-offset-2">
          See your fee schedule
        </Link>
        .
      </p>
    </>
  );
}
